// @ts-check
import { gql } from '@apollo/client/core';
import { Image } from '@react-pdf/renderer';
import { format } from 'date-fns';
import de from 'date-fns/locale/de-AT';
import React from 'react';
import {
  Box,
  Flex,
  Page,
  Product,
  Registration,
  Text,
  Title,
} from '../../pdf-design-system';
import openSansLight from '../../pdf-design-system/fonts/open-sans/300';
import openSansRegular from '../../pdf-design-system/fonts/open-sans/400';
// import openSansBold from '../../pdf-design-system/fonts/open-sans/600';
import sourceSansRegular from '../../pdf-design-system/fonts/source-sans-pro/400';
import sourceSansBold from '../../pdf-design-system/fonts/source-sans-pro/600';
import { BottleImagePlaceholder } from '../../pdf-design-system/Product';
import UserLocaleProvider from '../../Providers/UserLocaleProvider';
import Columns from './Columns';
import beforeRender from './LKEinreichung_beforeRender';
import messages from './locale/messages';
import Stack from './Stack';
import Terms from './Terms';
import Trait from './Trait';

const dateLocales = { de };

// We need to define the return types of useProduct and useRegistration because this can't be inferred automatically.
/**
 * A registration.
 *
 * @typedef {ReturnType<afterQuery>} Registration
 */

/**
 * A registeredProduct.
 *
 * @typedef {ReturnType<afterQuery>["registeredProducts"]["nodes"][number]} RegisteredProduct
 */

/**
 * Renders a single sheet per product. I'll probably also add a terms sheet at the end.
 *
 * @param {{
 *  data: import('./@types/LKEinreichung').LkBurgenlandEinreichungFragment;
 *  locale: import('@bottlebooks/graphql-server/src').ContentLocale;
 * }} props
 */
export default function LKBurgenlandEinreichung({ data, locale, ...rest }) {
  const registration = afterQuery(data);
  return (
    <UserLocaleProvider locale={locale} messages={messages}>
      <Registration.Provider value={registration}>
        {registration.registeredProducts.nodes?.map((registeredProduct) => (
          <Product.Provider
            key={registeredProduct.productId}
            value={registeredProduct}
          >
            <Page
              theme={theme}
              style={{ padding: '12.5mm', paddingBottom: '8mm' }}
              {...rest}
            >
              <Stack space={2}>
                <PageHeader />
                <Columns>
                  <Einreicher
                    // @ts-ignore span is processed by the Columns component.
                    span={3}
                  />
                  <Nummernblock />
                </Columns>
                <Wein />
                <Bestaetigungen locale={locale} />
              </Stack>
            </Page>
          </Product.Provider>
        ))}
        {/* <Terms /> */}
      </Registration.Provider>
    </UserLocaleProvider>
  );
}

function PageHeader() {
  /** @type {Registration} */
  const { collection } = Registration.useRegistration();
  const owner = collection?.owner;
  return (
    <Columns style={{ alignItems: 'flex-end' }}>
      <Box span={3} style={{ paddingBottom: '6mm' }}>
        <Text>{owner?.name}</Text>
        <Title>{collection?.name}</Title>
      </Box>
      {collection?.logo?.fixed?.src ? (
        <Image
          src={collection.logo.fixed.src}
          style={{ margin: 'auto', width: 122, height: 61 }}
        />
      ) : (
        <Box />
      )}
    </Columns>
  );
}

function Einreicher() {
  /** @type {Registration} */
  const { profile, betriebsnummer } = Registration.useRegistration();
  return (
    <Columns>
      {profile?.logo?.fixed?.src ? (
        <Image
          src={profile.logo.fixed.src}
          style={{ margin: 'auto', height: 123 }}
        />
      ) : (
        <Box />
      )}
      <Box span={2}>
        <Flex>
          <Trait.Label style={{ width: '25mm' }}>Einreicher</Trait.Label>
          <Box>
            <Text>{profile?.name}</Text>
            <Text>{profile?.street}</Text>
            <Text>
              {profile?.postalCode} {profile?.city}
            </Text>
          </Box>
        </Flex>
        <Flex style={{ marginVertical: '3mm' }}>
          <Trait.Label style={{ width: '25mm' }}>Betriebsnummer</Trait.Label>
          {betriebsnummer ? <Text>{betriebsnummer}</Text> : <Empty />}
        </Flex>
        <Flex>
          <Trait.Label style={{ width: '25mm' }}>Kontaktdaten</Trait.Label>
          <Box>
            <Text>{profile?.telephone || <Empty />}</Text>
            <Text>{profile?.email}</Text>
            <Text>{profile?.website}</Text>
          </Box>
        </Flex>
      </Box>
    </Columns>
  );
}

function Nummernblock() {
  /** @type {RegisteredProduct} */
  const product = Product.useProduct();
  return (
    <Box style={{ border: 0.5, textAlign: 'right' }}>
      <Trait
        label="Einreichnummer"
        style={{ paddingHorizontal: '2mm', borderBottom: 0.5 }}
      >
        <Text variant="bold">{value(product.einreichnummer) || <Empty />}</Text>
      </Trait>
      <Trait
        label="Übernahmenummer"
        style={{ paddingHorizontal: '2mm', borderBottom: 0.5 }}
      >
        {' '}
      </Trait>
      <Box style={{ paddingHorizontal: '2mm', borderBottom: 0.5 }}>
        <Trait label="Kostnummer"> </Trait>
      </Box>
      <Columns space={0}>
        <Box />
        <Trait
          label="Bewertung"
          style={{ paddingHorizontal: '2mm', borderLeft: 0.5 }}
        >
          {' '}
        </Trait>
      </Columns>
    </Box>
  );
}

function Wein() {
  /** @type {RegisteredProduct} */
  const { product, producer, ...custom } = Product.useProduct();
  return (
    <Columns>
      {product?.bottleImage?.fixed?.src ? (
        <Image
          src={product.bottleImage.fixed.src}
          style={{ marginHorizontal: 'auto' }}
        />
      ) : (
        <Box>
          <BottleImagePlaceholder
            width="3cm"
            height="13cm"
            style={{ marginHorizontal: 19 }} // TODO: marginHorizontal: 'auto' doesn't work, so we need to set this manually.
          />
        </Box>
      )}

      <Box span={3}>
        <Stack>
          <Box style={{ marginTop: '3mm' }}>
            <Columns>
              <Text span={2} style={{ marginBottom: '-2mm' }}>
                {producer.name}
              </Text>
              <Box style={{ color: '#666' }}>
                <Text variant="small" style={{ textAlign: 'right' }}>
                  {product.productId}
                </Text>
              </Box>
            </Columns>
            <Title variant="large" style={{ marginBottom: '5mm' }}>
              {product.name}
            </Title>
          </Box>

          {product.__typename === 'Wine' && (
            <Columns>
              <Stack>
                <Trait label="Weintyp">{product.wineType || <Empty />}</Trait>
                <Trait label="Farbe">{product.wineColor || <Empty />}</Trait>
                <Trait label="Gebiet">
                  {product.denomination || <Empty />}
                </Trait>
                <Trait label="Engere Herkunft">
                  {value(custom.engereHerkunft) || <Empty />}
                </Trait>
                <Trait label="Qualitätsstufe">
                  {product.classification || <Empty />}
                </Trait>
                <Trait label="Gewünschte Kategorie">
                  {value(custom.kategorie) || <Empty />}
                </Trait>
                <Trait label="Ried">{product.vineyard || <Empty />}</Trait>
              </Stack>
              <Stack>
                <Trait label="Alkohol">
                  {product.alcohol != null ? (
                    <>{product.alcohol}% Vol</>
                  ) : (
                    <Empty />
                  )}
                </Trait>

                <Trait label="Gesamtsäure">
                  {product.totalAcidity != null ? (
                    <>
                      {product.totalAcidity} {product.acidityUnit || 'g/l'}
                    </>
                  ) : (
                    <Empty />
                  )}
                </Trait>
                <Trait label="Restzucker">
                  {product.residualSugar != null ? (
                    <>{product.residualSugar} g/l</>
                  ) : (
                    <Empty />
                  )}
                </Trait>
                <Trait label="Restzuckergehalt">
                  {product.designation || <Empty />}
                </Trait>
                <Trait.Wrapper>
                  <Trait.Label>Rebsorten</Trait.Label>
                  {product.grapeVarieties?.length ? (
                    product.grapeVarieties.map((variety) => (
                      <Trait.Value key={variety.varietyName}>
                        {variety.percentage ? `${variety.percentage}% ` : ''}
                        {variety.varietyName}
                      </Trait.Value>
                    ))
                  ) : (
                    <Empty />
                  )}
                </Trait.Wrapper>
                <Box>
                  <Trait.Label>Betriebszertifizierungen</Trait.Label>
                  {producer.profile.certifications?.length ? (
                    producer.profile.certifications?.map((certification) => (
                      <Text key={certification}>{certification}</Text>
                    ))
                  ) : (
                    <Empty />
                  )}
                </Box>
              </Stack>
              <Stack>
                <Trait label="Prüfbescheid hochgeladen">
                  {custom.pruefbescheid?.rawText ? 'Ja' : <Empty />}
                </Trait>
                <Trait label="Prüfnummer">
                  {product.officialRegistrationNumber || <Empty />}
                </Trait>

                <Trait label="Gesamtproduktion">
                  {value(custom.gesamtproduktion) ? (
                    <>{value(custom.gesamtproduktion)} Liter</>
                  ) : (
                    <Empty />
                  )}
                </Trait>

                {product.bottleVariants?.map((variant, index) => (
                  <Stack key={index}>
                    <Trait label="Flaschengröße">
                      {variant.bottleSize || <Empty />}
                    </Trait>
                    <Trait label="Flaschenanzahl">
                      {variant.productionSize != null ? (
                        <>{variant.productionSize} Flaschen</>
                      ) : (
                        <Empty />
                      )}
                    </Trait>
                  </Stack>
                ))}
                <Trait label="Zertifiziert biologisch">
                  {product.isCertifiedOrganic === true ? (
                    'Ja'
                  ) : product.isCertifiedOrganic ? (
                    'Nein'
                  ) : (
                    <Empty />
                  )}
                </Trait>
                <Trait label="Ausbau">
                  {value(custom.ausbau) || <Empty />}
                </Trait>
              </Stack>
            </Columns>
          )}
        </Stack>
      </Box>
    </Columns>
  );
}

/**
 * This is a helper to work around the custom value type errors.
 * @returns {string | undefined}
 */
function value(customValue) {
  return customValue?.value;
}
// /**
//  * This is a helper to work around the custom value type errors.
//  * @returns {any[] | undefined}
//  */
// function values(customValue) {
//   return customValue?.values;
// }
/**
 * This is a helper to work around the custom value type errors.
 * @returns {string | undefined}
 */
function key(customValue) {
  return customValue?.key;
}

function Bestaetigungen({ locale }) {
  /** @type {RegisteredProduct} */
  const { product, producer, ...custom } = Product.useProduct();
  /** @type {Registration} */
  const { profile, collection } = Registration.useRegistration();
  return (
    <Stack>
      <Columns>
        <Stack>
          <YesNo value={key(custom.teilnahmebedingungenAkzeptiert) === 'ja'}>
            Ich akzeptiere die Teilnahmebedingungen:
          </YesNo>

          <YesNo value={value(custom.verzichtAufFlaschenaufkleber)}>
            Ich verzichte auf Flaschenaufkleber:{' '}
          </YesNo>

          <YesNo value={value(custom.verzichtAufFlaschenaufkleberFinalweine)}>
            Ich verzichte auf Flaschenaufkleber für Final-Weine:
          </YesNo>

          <YesNo
            value={value(custom.verzichtAufFlaschenaufkleberLandessiegerweine)}
          >
            Ich verzichte auf Flaschenaufkleber für Landessieger:
          </YesNo>
        </Stack>
        <Stack>
          <Flex>
            <YesNo value={value(custom.teilnahmeSalon)}>
              Ich nehme am Salon des Österreichischen Weins teil:
            </YesNo>
          </Flex>
          {value(custom.teilnahmeSalon) && (
            <Text variant="smallest">
              Teilnahmebedingungen sind, dass von Beerenauslese, Eiswein,
              Ausbruch, Trockenbeerenauslese mind. 500 Flaschen und von allen
              anderen Weinen mind. 1.000 Liter (1.300 Flaschen), zur Verfügung
              stehen. Für den Fall, dass mein Wein auf Grund des Kostergebnisses
              am "Salon Österr. Wein" teilnehmen kann, stelle ich diesen Wein
              unter den Bedingungen des "Salon Österr. Wein", welche ich zur
              Kenntnis nehme, zur Verfügung. Die Teilnahmegebühr für jeden in
              den Salon aufgenommen Wein oder Sekt beträgt € 200,00 netto.
            </Text>
          )}
        </Stack>
      </Columns>

      <Columns>
        <Stack>
          <YesNo value={key(custom.pruefbescheidbestaetigung) === 'ja'}>
            Ich bestätige, dass die Analysedaten (Alkohol, Gesamtzuckergehalt,
            Titrierbare Säure) dem hochgeladenen Prüfbescheid entsprechen:
          </YesNo>
          <Text variant="small">
            Ich versichere, dass alle Angaben wahrheitsgetreu sind und die
            angemeldete Probe den Bestimmungen des Österreichischen Weingesetzes
            entspricht. Die Teilnahme-Bedingungen nehme ich zur Kenntnis.
          </Text>
        </Stack>
        <Text variant="smallest">{collection.productInstructions}</Text>
      </Columns>
      <Columns style={{ paddingTop: '5mm' }}>
        <Text style={{ textAlign: 'right' }}>
          {profile.city}, am{' '}
          {format(new Date(), 'PPP', { locale: dateLocales[locale] })}
        </Text>
        <Text
          style={{
            borderBottom: 0.5,
            borderBottomStyle: 'dotted',
            color: '#666',
          }}
        >
          Unterschrift, Stempel
        </Text>
      </Columns>
    </Stack>
  );
}

function YesNo({ value, children, ...rest }) {
  if (value === true) {
    return (
      <Text variant="small" {...rest}>
        {children} Ja
      </Text>
    );
  }
  if (value === false) {
    return (
      <Text variant="small" {...rest}>
        <Text variant="small" style={{ textDecoration: 'line-through' }}>
          {children}
        </Text>{' '}
        Nein
      </Text>
    );
  }
  return (
    <Text variant="small" {...rest}>
      {children} <Empty />
    </Text>
  );
}

/** @type {import('../../components/Preview').AfterRenderEvent} */
LKBurgenlandEinreichung.afterRender = async (options) => {
  console.log('afterRender');
};

LKBurgenlandEinreichung.onRender = beforeRender;

/** @type  {import('@apollo/client').TypedDocumentNode<import('./@types/LKEinreichung').LkBurgenlandEinreichungFragment>}*/
LKBurgenlandEinreichung.fragment = gql`
  fragment LKBurgenlandEinreichung on RegistrationCatalogSection {
    registration {
      collectionId
      registrationId
      companyId
      registrationNumber: customFieldText(key: "registrationNumber")
      ...LKEinreichung_Einreicher
      collection {
        ...LKEinreichung_Collection
        ... on Event {
          eventId # needed for Apollo Client keyFields
        }
      }
      registeredProducts {
        nodes {
          ...LKEinreichung_Wein
          producer {
            ...LKEinreichung_Produzent
          }
        }
      }
    }
  }

  fragment LKEinreichung_Collection on Collection {
    name
    productInstructions(format: TEXT)
    logo {
      # 122ptx61pt in 300dpi (122/72*300)
      fixed(width: 508, height: 254, crop: PAD) {
        src
      }
    }
    # LK Burgenland:
    owner {
      name
      street
      postalCode
      city
      brandId # needed for apollo-client
    }
    collectionId # needed for apollo-client
    ... on Event {
      eventId # needed for apollo-client
    }
  }

  fragment LKEinreichung_Einreicher on SingleRegistration {
    stand {
      name
    }
    betriebsnummer: customFieldText(key: "betriebsnummer")
    profile {
      name
      street
      postalCode
      city
      telephone
      email
      logo {
        # 123ptx123pt in 300dpi
        fixed(width: 512, height: 512, crop: PAD) {
          src
        }
      }
      website
    }
  }

  fragment LKEinreichung_Produzent on RegisteredBrand {
    brandId
    name
    profile {
      certifications
    }
  }

  fragment LKEinreichung_Wein on RegisteredProduct {
    productId
    pruefbescheid: customFieldValue(key: "prufnummernbescheid") {
      __typename
      rawText
      ... on TextFieldValue {
        value
      }
    }
    einreichnummer: customFieldValue(key: "einreichnummer") {
      ... on TextFieldValue {
        value
      }
    }
    engereHerkunft: customFieldValue(key: "engereHerkunft") {
      ... on SelectFieldValue {
        value
      }
    }
    kategorie: customFieldValue(key: "kategorie") {
      ... on SelectFieldValue {
        value
      }
    }
    verzichtAufFlaschenaufkleber: customFieldValue(
      key: "verzichtAufFlaschenaufkleber"
    ) {
      ... on YesNoFieldValue {
        value
      }
    }
    verzichtAufFlaschenaufkleberFinalweine: customFieldValue(
      key: "verzichtAufFlaschenaufkleberFinalweine"
    ) {
      ... on YesNoFieldValue {
        value
      }
    }
    verzichtAufFlaschenaufkleberLandessiegerweine: customFieldValue(
      key: "verzichtAufFlaschenaufkleberLandessiegerweine"
    ) {
      ... on YesNoFieldValue {
        value
      }
    }
    teilnahmeSalon: customFieldValue(key: "teilnahmeSalon") {
      ... on YesNoFieldValue {
        value
      }
    }
    gesamtproduktion: customFieldValue(key: "gesamtproduktion") {
      ... on TextFieldValue {
        value
      }
    }
    ausbau: customFieldValue(key: "ausbau") {
      ... on SelectFieldValue {
        value
      }
    }
    teilnahmebedingungenAkzeptiert: customFieldValue(
      key: "teilnahmebedingungenAkzeptiert"
    ) {
      ... on SelectFieldValue {
        key
      }
    }
    pruefbescheidbestaetigung: customFieldValue(
      key: "pruefbescheidbestaetigung"
    ) {
      ... on SelectFieldValue {
        value
        key
      }
    }

    product {
      __typename
      productId
      name
      ... on Wine {
        wineType
        alcohol
        residualSugar
        totalAcidity
        acidityUnit
        designation
        classification
        denomination
        wineColor
        isCertifiedOrganic
        vineyard
        grapeVarieties {
          percentage
          varietyName
        }
        officialRegistrationNumber
      }

      internationalCertifications {
        name
      }
      localCertifications {
        name
      }
      bottleImage {
        # 123x369pt in 300dpi
        fixed(width: 513, height: 1539, crop: PAD, gravity: NORTH) {
          src
        }
      }
      bottleVariants {
        bottleSize
        productionSize
      }
    }
  }
`;

/** @param {import('./@types/LKEinreichung').LkBurgenlandEinreichungFragment} data */
function afterQuery({ registration }) {
  return registration;
}

/**
 * @typedef {import('../../pdf-design-system/useTheme').BottlebooksPDFTheme["theme"] & {
 *  components?: { Traits?: import('./Traits').TraitsDesign; };
 * space?: Record<string, number | string>;
 * }} Theme
 */

/** @type {Theme} */
export const theme = {
  space: {
    0: 0,
    1: '1mm',
    2: '2mm',
    3: '4mm',
    4: '8mm',
  },
  body: { ...openSansRegular },
  components: {
    Text: {
      base: { ...sourceSansRegular, fontSize: 10, lineHeight: 1.5 },
      default: {},
      bold: { ...sourceSansBold },
      small: { fontSize: 8 },
      smallest: { fontSize: 6 },
    },
    Title: {
      base: { ...openSansLight },
      default: { fontSize: 20, lineHeight: 1 },
      large: { fontSize: 28, lineHeight: 1.07 },
    },

    Traits: {
      styles: {
        label: {
          ...openSansRegular,
          textTransform: 'uppercase',
          fontSize: 6,
          lineHeight: 2.5,
          color: '#666',
          // `alignItems: baseline` isn't implemented correctly in react-pdf.
          // So we need to fix the label for 'baseline' adjustment:
          // We push the label down by the difference between the label and value font sizes (11pt - 8pt).
          paddingTop: 3,
          marginBottom: -3,
        },
      },
    },
  },
};

/** @param {{ style?: import('@react-pdf/types').Style; [x: string]: any }} props */
function Empty({ style, ...rest }) {
  return (
    <Text
      variant="small"
      style={[{ color: '#666', lineHeight: 1.875 }, style]}
      {...rest}
    >
      — keine Angabe —
    </Text>
  );
}
